import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { BiPlus } from 'react-icons/bi';
import BackButton from '~/components/BackButton';
import Loader from '~/components/Loader';
import { useGetProduct, useGetProductsList, useUpdateProduct } from '~/hooks/products';
import { InputText, StyledSelect } from '~/lib/HooksFormFields';
import type { IOption } from '~/types/options';
import { createFile, deleteFile } from '~/api/files';
import type { IFile } from '~/types/files';
import { downloadFile } from '~/utils';
import styles from './detail.module.scss';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'underline'],
    [{ list: 'bullet' }],
    ['clean'],
  ],
};

const DetailProduct = () => {
  const { id } = useParams();
  const { data: product, isLoading } = useGetProduct(id || '');
  const { data: productsList } = useGetProductsList();
  const { mutateAsync: updateProduct } = useUpdateProduct();

  const {
    control,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      title: '',
      productType: '',
      descriptionHtml: '',
      externalLinks: [] as { label?: string; url: string }[],
    },
  });

  const productTypeOptions = useMemo(() => {
    const options: IOption[] = productsList?.productTypes || [];
    const current = product?.productType;
    if (current && !options.some((o) => o.value === current)) {
      return [{ label: current, value: current }, ...options];
    }
    return options;
  }, [productsList?.productTypes, product?.productType]);

  const [existingImageFile, setExistingImageFile] = useState<IFile | null>(null);
  const [existingNoticeFiles, setExistingNoticeFiles] = useState<IFile[]>([]);

  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingNoticeFiles, setPendingNoticeFiles] = useState<File[]>([]);

  const [removedExistingImage, setRemovedExistingImage] = useState(false);
  const [removedNoticeFileIds, setRemovedNoticeFileIds] = useState<string[]>([]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'externalLinks',
  });

  const token = localStorage.getItem('token') || '';
  const fileUrl = useCallback((file: IFile) => (
    `${process.env.REACT_APP_API_URL}/files/public/${file.path}?token=${token}`
  ), [token]);

  useEffect(() => {
    if (!product) return;
    reset({
      title: product.title || '',
      productType: product.productType || '',
      descriptionHtml: product.descriptionHtml || '',
      externalLinks: product?.externalLinks || [],
    });

    setExistingImageFile(product?.imageFile || null);
    setExistingNoticeFiles(product?.noticeFiles || []);
    setPendingImageFile(null);
    setPendingNoticeFiles([]);
    setRemovedExistingImage(false);
    setRemovedNoticeFileIds([]);
  }, [product, reset]);

  const submit = useCallback(async (data: any) => {
    if (!product?._id) return;

    const previousImageId = existingImageFile?._id || null;

    const uploadedImage = pendingImageFile
      ? (await createFile(pendingImageFile)).file
      : null;

    const uploadedNotices = pendingNoticeFiles.length > 0
      ? (await Promise.all(
        pendingNoticeFiles.map(async (f) => (await createFile(f))?.file),
      )).filter(Boolean) as IFile[]
      : [];

    const nextImageId = uploadedImage?._id
      ?? (removedExistingImage ? null : (existingImageFile?._id || null));

    const keptExistingNotices = existingNoticeFiles
      .filter((f) => !removedNoticeFileIds.includes(f._id));
    const nextNoticeIds = [
      ...keptExistingNotices.map((f) => f._id),
      ...uploadedNotices.map((f) => f._id),
    ];

    await updateProduct({
      _id: product._id,
      title: data.title,
      productType: data.productType || '',
      descriptionHtml: data.descriptionHtml,
      imageFile: nextImageId,
      noticeFiles: nextNoticeIds,
      externalLinks: data.externalLinks || [],
    });

    // Apply local state after successful save
    setExistingImageFile(uploadedImage ?? (removedExistingImage ? null : existingImageFile));
    setExistingNoticeFiles([...keptExistingNotices, ...uploadedNotices]);
    setPendingImageFile(null);
    setPendingNoticeFiles([]);
    setRemovedExistingImage(false);
    setRemovedNoticeFileIds([]);

    // Cleanup removed server files only after product is saved
    const fileIdsToDelete: string[] = [
      ...(removedExistingImage && previousImageId ? [previousImageId] : []),
      ...removedNoticeFileIds,
      ...(uploadedImage && previousImageId ? [previousImageId] : []),
    ];

    await Promise.all(fileIdsToDelete.map(async (fileId) => {
      try {
        await deleteFile(fileId);
      } catch (e) {
        // ignore: product is saved; orphan cleanup can fail without blocking user
      }
    }));
  }, [
    product?._id,
    updateProduct,
    existingImageFile,
    existingNoticeFiles,
    pendingImageFile,
    pendingNoticeFiles,
    removedExistingImage,
    removedNoticeFileIds,
  ]);

  const canShowFiles = useMemo(() => Boolean(token), [token]);

  return (
    <div className={styles.detailProduct}>
      {isLoading && <Loader />}
      {product && (
        <>
          <form id='product-form' onSubmit={handleSubmit(submit)}>
            <div className={styles.content}>
              <div>
                <div className={styles.header}>
                  <div className={styles.titlePage}>
                    <BackButton />
                    <h1>{product.title}</h1>
                  </div>
                  <div className={styles.actions}>
                    <button type='submit'>Sauvegarder</button>
                  </div>
                </div>

                <div className={styles.containerField}>
                  <InputText
                    label='Nom'
                    name='title'
                    control={control}
                    rules={{ required: 'Le nom est requis' }}
                  />
                </div>
                <div className={styles.containerField}>
                  <StyledSelect
                    label='Type de produit'
                    name='productType'
                    control={control}
                    options={productTypeOptions}
                    isCreatable
                    isClearable
                    isSearchable
                    placeholder='Sélectionner ou créer un type'
                    formatCreateLabel={(inputValue) => `Ajouter le type "${inputValue}"`}
                  />
                </div>
                <div className={styles.containerField}>
                  <label className={styles.fieldLabel}>Description</label>
                  <div className={styles.richText}>
                    <Controller
                      control={control}
                      name='descriptionHtml'
                      render={({ field }) => (
                        <ReactQuill
                          theme='snow'
                          modules={quillModules}
                          value={field.value || ''}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div>
                <section className={styles.section}>
                  <h4>Médias</h4>

                  <div className={styles.containerField}>
                    <label className={styles.fieldLabel}>Image d’illustration</label>
                    <div className={styles.inlineRow}>
                      <label className={styles.addFile} htmlFor='product-image'>
                        {BiPlus({})}
                        Ajouter un fichier
                      </label>
                      <input
                        id='product-image'
                        type='file'
                        accept='image/*'
                        className={styles.hiddenInput}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setPendingImageFile(file);
                          setRemovedExistingImage(false);
                          // allow selecting the same file again later
                          e.target.value = '';
                        }}
                      />
                      {pendingImageFile && (
                        <div className={styles.fileRow}>
                          <span>{pendingImageFile.name}</span>
                          <button
                            type='button'
                            className='secondary'
                            onClick={() => setPendingImageFile(null)}
                          >
                            Retirer
                          </button>
                        </div>
                      )}
                      {!pendingImageFile && existingImageFile && !removedExistingImage && (
                        <div className={styles.fileRow}>
                          <span>{existingImageFile.name}</span>
                          {canShowFiles && (
                            <button
                              type='button'
                              className='secondary'
                              onClick={() => downloadFile(
                                fileUrl(existingImageFile),
                                existingImageFile.name,
                              )}
                            >
                              Télécharger
                            </button>
                          )}
                          <button
                            type='button'
                            className='secondary'
                            onClick={() => setRemovedExistingImage(true)}
                          >
                            Retirer
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.containerField}>
                    <label className={styles.fieldLabel}>Notices (PDF)</label>
                    <div className={styles.inlineRow}>
                      <label className={styles.addFile} htmlFor='product-notices'>
                        {BiPlus({})}
                        Ajouter un fichier
                      </label>
                      <input
                        id='product-notices'
                        type='file'
                        accept='application/pdf'
                        multiple
                        className={styles.hiddenInput}
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length === 0) return;
                          setPendingNoticeFiles((prev) => [...prev, ...files]);
                          e.target.value = '';
                        }}
                      />
                    </div>

                    {(existingNoticeFiles.length > 0 || pendingNoticeFiles.length > 0) && (
                      <div className={styles.filesList}>
                        {existingNoticeFiles
                          .filter((f) => !removedNoticeFileIds.includes(f._id))
                          .map((f) => (
                            <div key={`notice-existing-${f._id}`} className={styles.fileRow}>
                              <span>{f.name}</span>
                              {canShowFiles && (
                                <button
                                  type='button'
                                  className='secondary'
                                  onClick={() => downloadFile(fileUrl(f), f.name)}
                                >
                                  Télécharger
                                </button>
                              )}
                              <button
                                type='button'
                                className='secondary'
                                onClick={() => setRemovedNoticeFileIds((prev) => [...prev, f._id])}
                              >
                                Retirer
                              </button>
                            </div>
                          ))}

                        {pendingNoticeFiles.map((f, idx) => (
                          <div key={`notice-pending-${idx}-${f.name}`} className={styles.fileRow}>
                            <span>{f.name}</span>
                            <button
                              type='button'
                              className='secondary'
                              onClick={() => setPendingNoticeFiles(
                                (prev) => prev.filter((_, i) => i !== idx),
                              )}
                            >
                              Retirer
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </section>

                <section className={styles.section}>
                  <h4>Liens externes</h4>

                  <div className={styles.containerField}>
                    <button
                      type='button'
                      className='secondary'
                      onClick={() => append({ label: '', url: '' })}
                    >
                      Ajouter un lien
                    </button>
                  </div>

                  {fields.map((field, index) => (
                    <div key={field.id} className={styles.linkRow}>
                      <div className={styles.linkFields}>
                        <InputText
                          label='Label'
                          name={`externalLinks.${index}.label`}
                          control={control}
                        />
                        <InputText
                          label='URL'
                          name={`externalLinks.${index}.url`}
                          control={control}
                          rules={{ required: 'URL requise' }}
                          placeholder='https://...'
                        />
                      </div>
                      <div className={styles.linkActions}>
                        <button
                          type='button'
                          className='secondary'
                          onClick={() => remove(index)}
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </section>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default DetailProduct;
